/**
 * In-memory cache for the `gym_notebook_exercise_history` localStorage blob.
 *
 * Why this exists:
 *   `TrainingInProgress.saveExerciseToHistory()` runs on every set-tap during
 *   a workout. It used to read + JSON.parse the entire history blob, mutate
 *   it, then JSON.stringify + write it back, then POST to the backend. For a
 *   long-time user the blob is 100-500 KB — that's 30-100 ms of blocking
 *   work per tap on iPhone Safari (contributing to the iOS PWA freeze bug
 *   fixed in iter181).
 *
 * What this module does:
 *   - Lazy-parses the blob into memory on first read (paying the cost once
 *     per session instead of per tap).
 *   - Returns the *cached* reference on subsequent `getExerciseHistory()`
 *     calls. Callers must treat it as read-only or pass in a fresh copy.
 *   - Batches writes with a 250 ms debounce, and coalesces bursts into a
 *     single JSON.stringify + localStorage.setItem.
 *   - Listens for cross-tab `storage` events and invalidates the cache so
 *     stale in-memory state can't be persisted over data from another tab.
 *
 * Public API is intentionally tiny — components that already work with
 * localStorage.getItem directly can migrate opportunistically. The hot
 * path (per-set-tap in TrainingInProgress) is the one that matters.
 */

export type ExerciseHistoryEntry = {
  date?: string;
  sets?: any[];
  weightUnit?: string;
  week?: number;
  [k: string]: any;
};

export type ExerciseHistoryMap = Record<string, ExerciseHistoryEntry[]>;

const STORAGE_KEY = 'gym_notebook_exercise_history';
const DEBOUNCE_MS = 250;
const MAX_PER_EXERCISE = 20;

let cache: ExerciseHistoryMap | null = null;
let pendingWrite: ReturnType<typeof setTimeout> | null = null;

// Cross-tab invalidation: if another tab writes to the key, drop the cache
// so the next read re-parses fresh data instead of clobbering it.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      cache = null;
      if (pendingWrite) {
        clearTimeout(pendingWrite);
        pendingWrite = null;
      }
    }
  });
}

function readFromStorage(): ExerciseHistoryMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function scheduleWrite(): void {
  if (pendingWrite) return; // already queued
  pendingWrite = setTimeout(() => {
    pendingWrite = null;
    if (!cache) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch (err) {
      // Quota exceeded or private mode — log and drop.
      console.warn('[exerciseHistoryCache] Failed to persist:', err);
    }
  }, DEBOUNCE_MS);
}

/** Returns the cached exercise history object (lazy-loaded on first call). */
export function getExerciseHistory(): ExerciseHistoryMap {
  if (cache === null) {
    cache = readFromStorage();
  }
  return cache;
}

/**
 * Append one entry for `exerciseName` and cap the list at
 * `MAX_PER_EXERCISE` (newest first). Writes are debounced.
 */
export function appendExerciseEntry(exerciseName: string, entry: ExerciseHistoryEntry): void {
  if (!exerciseName) return;
  if (cache === null) cache = readFromStorage();
  const existing = cache[exerciseName] || [];
  cache[exerciseName] = [entry, ...existing].slice(0, MAX_PER_EXERCISE);
  scheduleWrite();
}

/**
 * Replace the entire history (used by cross-device sync / restore paths).
 * Invalidates any pending write and immediately persists.
 */
export function replaceExerciseHistory(next: ExerciseHistoryMap): void {
  cache = next && typeof next === 'object' ? next : {};
  if (pendingWrite) {
    clearTimeout(pendingWrite);
    pendingWrite = null;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('[exerciseHistoryCache] Failed to persist on replace:', err);
  }
}

/** Force-flush any pending debounced write. Used on page hide / unload. */
export function flushExerciseHistory(): void {
  if (pendingWrite) {
    clearTimeout(pendingWrite);
    pendingWrite = null;
  }
  if (cache) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch (err) {
      console.warn('[exerciseHistoryCache] Failed to flush:', err);
    }
  }
}

/**
 * Drop the in-memory cache without writing anything. Use after a caller
 * has already done `localStorage.removeItem` — otherwise a subsequent read
 * would return the stale in-memory data and a subsequent write would
 * clobber the empty key. Also cancels any pending debounced write.
 */
export function clearExerciseHistoryCache(): void {
  cache = null;
  if (pendingWrite) {
    clearTimeout(pendingWrite);
    pendingWrite = null;
  }
}

// Flush before the page is hidden so a pending debounce doesn't lose data
// if the user closes the tab or the browser suspends the PWA.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushExerciseHistory();
  });
  window.addEventListener('pagehide', flushExerciseHistory);
}
